import { useQuery, useReactiveVar } from '@apollo/client';
import { Box, Button, CircularProgress } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import allViews from 'src/api/viewModels/allViews';
import { MediaCardActionMenuContainer } from 'src/components/MediaGrid/ActionMenu';
import MediaPreviewModalWithProxy from 'src/components/MediaPreviewModalWithProxy';
import PDFPreview from 'src/components/PDF/PDFPreview';
import {
  ADD_TO_GALLERY_SLUG,
  HOME_PATH,
  MEDIA_PATH,
  PDF,
  SAMPLE_DOWNLOAD_LINK,
  SHARED_MEDIA_PATH,
} from 'src/constants';
import UpdateGalleryDialog from 'src/Gallery/dialogs/UpdateGalleryDialog';
import { AssetActionsPermMenu } from 'src/HomePage/DrawerAndPushedContents/PushedContents/AkomiMediaCardGrid/AkomiMediaCard/MediaCardActionsIcon/AssetActionsPermMenu';
import MediaRouterComponent from 'src/HomePage/DrawerAndPushedContents/PushedContents/AkomiMediaCardGrid/AkomiMediaCard/MediaRouterComponent';
import { getMetadataValue } from 'src/helpers/common';
import { genMediaUrl } from 'src/helpers/generateCustomFields';
import { useMediaQueries } from 'src/hooks/media';
import styles from 'src/LargeVideoPage/LargeVideoPageStyles';
import { SharedLinkContext } from 'src/SharedLinkPage/context';
import SharedLinkActionsPerms from 'src/SharedLinkPage/SharedLinkActionsPerms';
import viewStateVar from 'src/store/viewState';
import MediaPageSkeleton from './MediaPageSkeleton';
import MediaPageTabs from './MediaPageTabs';

interface MarkerData {
  time?: number;
  text?: string;
  duration?: number;
}

interface VideoRef {
  current: HTMLVideoElement | null;
}

interface MediaPageProps {
  onFwd?: () => void;
  onBack?: () => void;
  fwdDisabled?: boolean;
  backDisabled?: boolean;
  showNavButtons?: boolean;
  itemId?: string;
  isShared?: boolean;
}

interface AssetUpdateData {
  assetId: string;
  timestamp: number;
  processedBy?: string[];
}

// Modified LibraryMedia component
const MediaPage = ({
  onFwd,
  onBack,
  fwdDisabled,
  backDisabled,
  showNavButtons,
  itemId,
  isShared = false,
}: MediaPageProps): React.JSX.Element => {
  const classes = styles();
  const { linkInfo } = useContext(SharedLinkContext);
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const queryTime = params.get('t');
  const tab = params.get('tab');

  const viewState = useReactiveVar(viewStateVar);
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(false);
  const [videoRef, setVideoRef] = useState<VideoRef | null>(null);
  const [marker, setMarker] = useState<MarkerData>({});

  const { assetMetaData, assetMetaDataLoading, assetMetaDataError, formatData, proxyFormat, refetchAssetMetaData } =
    useMediaQueries(itemId, isShared);

  // Listen for asset updates and refetch data when this specific asset is updated
  useEffect(() => {
    const componentId = `media-page-${itemId}-${Math.random()}`;

    const handleAssetUpdate = (): void => {
      const updateData = localStorage.getItem('assetUpdated');
      if (updateData && refetchAssetMetaData) {
        try {
          const { assetId, timestamp, processedBy = [] }: AssetUpdateData = JSON.parse(updateData);

          // Check if this is for our asset and we haven't processed it yet
          if (assetId === itemId && !processedBy.includes(componentId)) {
            // Refetch the asset metadata when this asset is updated
            refetchAssetMetaData();

            // Mark this component as having processed the update
            const updatedProcessedBy = [...processedBy, componentId];
            localStorage.setItem(
              'assetUpdated',
              JSON.stringify({
                assetId,
                timestamp,
                processedBy: updatedProcessedBy,
              }),
            );

            // Clean up old updates after 5 seconds
            setTimeout(() => {
              const currentData = localStorage.getItem('assetUpdated');
              if (currentData) {
                const parsed: AssetUpdateData = JSON.parse(currentData);
                if (parsed.timestamp === timestamp) {
                  localStorage.removeItem('assetUpdated');
                }
              }
            }, 5000);
          }
        } catch {
          // Handle legacy format or corrupted data
          const updatedAssetId = updateData;
          if (updatedAssetId === itemId) {
            refetchAssetMetaData();
            localStorage.removeItem('assetUpdated');
          }
        }
      }
    };

    // Check immediately on mount
    handleAssetUpdate();

    // Set up an interval to check for updates (fallback for same-tab updates)
    const interval = setInterval(handleAssetUpdate, 500);

    // Also listen for storage events (for cross-tab updates)
    const handleStorageChange = (e: StorageEvent): void => {
      if (e.key === 'assetUpdated') {
        handleAssetUpdate();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [itemId, refetchAssetMetaData]);

  useEffect(() => {
    if (queryTime && videoRef?.current) {
      videoRef.current.currentTime = parseFloat(queryTime);
    }
  }, [queryTime, videoRef?.current]);

  const { loading } = useQuery(allViews, {
    variables: { viewName: 'Library' },
    fetchPolicy: 'cache-and-network',
  });

  const backButtonHandler = (): void => {
    // EditView's useBlocker handles the unsaved-changes prompt.
    // EditView's unmount cleanup calls exitEditMode() automatically.
    window.history.back();
  };

  const goHomeHandler = (): void => {
    navigate({ to: HOME_PATH });
  };

  if (loading || assetMetaDataLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height={'75vh'} width={'100%'}>
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  const title = getMetadataValue(assetMetaData as Record<string, unknown> | null | undefined, 'title');
  const fileName = getMetadataValue(assetMetaData as Record<string, unknown> | null | undefined, 'name');
  const assetName = title || fileName;
  const mimetype = getMetadataValue(formatData as Record<string, unknown> | null | undefined, 'mimetype');
  const isPDF = mimetype?.includes(PDF);

  const getVideoRef = (ref: VideoRef): void => {
    setVideoRef(ref);
  };

  if (assetMetaDataError) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height={'75vh'}
        width={'100%'}
      >
        <div className={classes.assetNotFound}>Asset not found</div>
        <Button onClick={goHomeHandler} variant="contained" color="primary">
          Library
        </Button>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>{assetName}</title>
      </Helmet>
      <MediaPageSkeleton
        title={assetName || ''}
        // For shared asset page, this is available only if it belongs to shared gallery
        onGoBack={!isShared || linkInfo?.collectionId ? backButtonHandler : undefined}
        onFwd={onFwd}
        onBack={onBack}
        fwdDisabled={fwdDisabled}
        backDisabled={backDisabled}
        showNavButtons={showNavButtons}
      >
        <div style={{ position: 'relative' }}>
          {isShared ? (
            <MediaCardActionMenuContainer>
              <SharedLinkActionsPerms itemId={itemId || ''} />
            </MediaCardActionMenuContainer>
          ) : (
            <AssetActionsPermMenu itemId={itemId || ''} />
          )}
          {isPDF ? (
            <PDFPreview
              pdfUrl={genMediaUrl(isShared ? SHARED_MEDIA_PATH : MEDIA_PATH, proxyFormat?.id)}
              downloadLink={SAMPLE_DOWNLOAD_LINK}
              isOnMediaPage={true}
            />
          ) : (
            <MediaRouterComponent
              itemId={itemId || ''}
              setPlayer={undefined}
              getVideoRef={getVideoRef as unknown as (ref: HTMLVideoElement) => void}
              shared={isShared}
              videoJsProps={undefined}
              onImageClick={() => setShowPreview(true)}
              marker={marker}
              fullResolution
            />
          )}
        </div>
        <MediaPageTabs
          defaultTab={tab}
          player={videoRef?.current || null}
          clipId={itemId || ''}
          edit={!!viewState.editClip}
          shared={isShared}
          setMarker={setMarker}
        />
      </MediaPageSkeleton>

      {showPreview && (
        <MediaPreviewModalWithProxy itemId={itemId || ''} isShared={isShared} onClose={() => setShowPreview(false)} />
      )}
      {viewState.globalAction === ADD_TO_GALLERY_SLUG && (
        <UpdateGalleryDialog
          isOpen={viewState.globalAction === ADD_TO_GALLERY_SLUG}
          onClose={() => viewStateVar({ ...viewState, globalAction: null })}
          assetIds={[itemId || '']}
        />
      )}
    </React.Fragment>
  );
};

export default MediaPage;
